/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext } from 'react';
import styled from 'styled-components';
import { ContactImportance } from '@bb/users';
import {
  saveUserDefaultContact,
  applyUserDefaultContact,
} from 'api/UserController';
import errorReporter from '@app/errorReporter';
import { Modal, Title, Button, breakpoints, message } from 'components';

import FormContext from '../FormContext';
import ContactModalContext from './ContactModalContext';
import Details from './Details';
import Notifications from './Notifications';
import Permissions from './Permissions';

export type ContactModalProps = {
  onClose?: () => void;
};

const ContactModal: React.FC<ContactModalProps> = ({ onClose }) => {
  const { userId, setContacts } = useContext(FormContext);
  const {
    visible,
    contact,
    modeType,
    allValid,
    isSaving,
    isApplyingSettings,
    setIsSaving,
    setIsApplyingSettings,
  } = useContext(ContactModalContext);
  const isNew = modeType === 'new';
  const title = `${isNew ? 'Add' : 'Edit'} Contact`;

  const handleSave = async () => {
    if (isSaving || isApplyingSettings) return;
    try {
      const processedContact = {
        ...contact,
        rep: contact.contactId,
        importance: ContactImportance[contact.importance],
      };
      const params = {
        userId,
      };
      setIsSaving(true);
      await saveUserDefaultContact(params, processedContact);
      setIsSaving(false);

      message.success(`Contact ${isNew ? 'added' : 'updated'} successfully`);
      if (isNew) setContacts(cur => [...cur, contact]);
      openApplyModal();
    } catch (err) {
      errorReporter.error(
        `Failed to ${isNew ? 'add' : 'update'} contact: ${err}`,
      );
      onClose();
    }
  };

  const openApplyModal = async () => {
    Modal.confirm({
      title: 'Add contact to existing and new listings?',
      content:
        'Would you like to apply this contact to all listing configurations? or just to new listings?',
      cancelText: 'Only New Listings',
      okText: 'Existing and New Listings',
      onOk: async () => {
        try {
          const applySettings = {
            userId,
            isEdit: modeType === 'edit',
            ...contact,
            rep: contact.contactId,
            importance: ContactImportance[contact.importance],
          };

          setIsApplyingSettings(true);
          Modal.destroyAll();
          await applyUserDefaultContact(applySettings);
          setIsApplyingSettings(false);

          message.success(`Listings updated successfully`);
        } catch (err) {
          errorReporter.error(`Failed to apply contact: ${err}`);
        }
        onClose();
      },
      onCancel: onClose,
      width: 460,
      centered: true,
    });
  };

  return (
    <Modal
      closable={false}
      open={visible}
      title={<StyledTitle>{title}</StyledTitle>}
      width={'min(900px, 80vw)'}
      destroyOnClose
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            disabled={!allValid}
            onClick={handleSave}
            loading={isSaving || isApplyingSettings}
          >
            Save
          </Button>
        </>
      }
    >
      <StyledContent>
        <Details />
        <Notifications />
        <Permissions />
      </StyledContent>
    </Modal>
  );
};

export default ContactModal;

const StyledContent = styled.div`
  display: grid;
  grid-template-columns: 7fr 5fr;
  grid-template-rows: auto 1fr;
  column-gap: 64px;
  row-gap: 64px;

  @media (max-width: ${breakpoints.md}px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
`;

const StyledTitle = styled(Title)`
  && {
    margin-bottom: 0;
    font-size: 32px;
    text-align: center;
  }
`;
